import { DialogComponent } from '@theme/dialog';

/**
 * Check URL for contact form success and update button state
 */
function checkContactSuccess() {
  const urlParams = new URLSearchParams(window.location.search);
  
  if (urlParams.get('contact_posted') === 'true') {
    // Remove hash from URL for ALL submissions (prevents scroll to #contact_form)
    if (window.location.hash) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState({}, '', url.toString());
    }
    
    // For modal submissions, swap buttons (hide original, show success)
    const productId = sessionStorage.getItem('poa_enquiry_product_id');
    if (productId) {
      const originalButton = /** @type {HTMLButtonElement | null} */ (document.querySelector(`button[data-product-id="${productId}"][on\\:click*="poa-enquiry-modal"]`));
      const successButton = /** @type {HTMLButtonElement | null} */ (document.querySelector(`button[data-poa-success-button="${productId}"]`));
      
      if (originalButton && successButton) {
        originalButton.style.display = 'none';
        successButton.style.display = '';  // Remove inline style, let CSS take over
      }
      sessionStorage.removeItem('poa_enquiry_product_id');
    }
  }
}

// Check for success on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkContactSuccess);
} else {
  checkContactSuccess();
}

/**
 * @typedef {object} ProductData
 * @property {string} id
 * @property {string} title
 * @property {string} handle
 * @property {string} url
 * @property {string} variantId
 * @property {string} variantSku
 * @property {string} variantTitle
 */

/**
 * @typedef {object} POAEnquiryModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} errorText
 * @property {HTMLElement} formContainer
 */

/**
 * POA Enquiry Modal Component
 * 
 * Extends DialogComponent with custom scroll lock using overflow: hidden
 * instead of the theme's broken position: fixed technique.
 * 
 * @extends {DialogComponent}
 */
export class POAEnquiryModal extends DialogComponent {
  requiredRefs = ['dialog', 'closeButton', 'successMessage', 'errorMessage', 'errorText', 'formContainer'];
  
  #savedScrollY = 0;
  #savedOverflow = '';

  connectedCallback() {
    super.connectedCallback();
    
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.#handleFormSubmit.bind(this));
    }
  }

  /**
   * Custom showDialog with overflow-based scroll lock
   * @param {Event} [event] - Click event from trigger button (optional)
   */
  showDialog(event) {
    const { dialog } = this.refs;
    if (dialog.open) return;
    
    // Extract product data
    let productData = null;
    if (event && event.target) {
      const button = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('[data-product-id]'));
      if (button && button.dataset) {
        productData = {
          id: button.dataset.productId || '',
          title: button.dataset.productTitle || '',
          handle: button.dataset.productHandle || '',
          url: button.dataset.productUrl || '',
          variantId: button.dataset.variantId || '',
          variantSku: button.dataset.variantSku || '',
          variantTitle: button.dataset.variantTitle || '',
        };
        
        // Store product ID for button update after form submission
        sessionStorage.setItem('poa_enquiry_product_id', productData.id);
      }
    }
    
    // Save scroll position and lock scroll with overflow: hidden
    this.#savedScrollY = window.scrollY;
    this.#savedOverflow = document.documentElement.style.overflow;
    
    requestAnimationFrame(() => {
      // Lock scroll (prevents scrolling while modal is open)
      document.documentElement.style.overflow = 'hidden';
      
      // Open dialog
      dialog.showModal();
      this.dispatchEvent(new CustomEvent('dialog-open'));
      
      // Add event listeners
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      
      // Reset form and populate with product data
      requestAnimationFrame(() => {
        this.#resetFormState();
        if (productData) {
          this.#populateFormFields(productData);
        }
      });
    });
  }
  
  /**
   * Click handler (from parent DialogComponent)
   * @param {MouseEvent} event - Click event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[on\\:click*="closeDialog"]')) {
      this.closeDialog();
    }
  };
  
  /**
   * Keydown handler (from parent DialogComponent)
   * @param {KeyboardEvent} event - Keydown event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.closeDialog();
    }
  };
  
  /**
   * Custom closeDialog with scroll restore
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    
    // Remove event listeners
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    
    // Add closing animation class
    dialog.classList.add('dialog-closing');
    
    // Wait for animation (300ms matches theme animation speed)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Restore scroll and close
    document.documentElement.style.overflow = this.#savedOverflow;
    window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });
    
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new CustomEvent('dialog-close'));
  };

  /**
   * Resets form state (clears success/error, shows form)
   */
  #resetFormState() {
    const { successMessage, errorMessage, formContainer } = this.refs;
    const form = this.querySelector('form');
    
    if (form) form.reset();
    if (formContainer) /** @type {HTMLElement} */ (formContainer).style.display = 'block';
    if (successMessage) /** @type {HTMLElement} */ (successMessage).style.display = 'none';
    if (errorMessage) /** @type {HTMLElement} */ (errorMessage).style.display = 'none';
  }

  /**
   * Populates hidden form fields and prefill message
   * @param {ProductData} productData - Product information from trigger
   */
  #populateFormFields(productData) {
    const form = this.querySelector('form');
    if (!form) return;

    const fields = {
      'contact[product_title]': productData.title,
      'contact[product_url]': productData.url,
      'contact[product_handle]': productData.handle,
      'contact[variant_title]': productData.variantTitle,
      'contact[variant_sku]': productData.variantSku,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = /** @type {HTMLInputElement | null} */ (form.querySelector(`input[name="${name}"]`));
      if (input) input.value = value || '';
    });

    const messageTextarea = /** @type {HTMLTextAreaElement | null} */ (form.querySelector('textarea[name="contact[body]"]'));
    if (messageTextarea && messageTextarea.dataset.prefill) {
      messageTextarea.value = messageTextarea.dataset.prefill
        .replace('{PRODUCT_TITLE}', productData.title)
        .replace('{VARIANT_SKU}', productData.variantSku || '');
    }
  }

  /**
   * Handles form submission with HTML5 validation
   * Uses native form submission - button updates on page reload
   * @param {Event} event - Form submit event
   */
  #handleFormSubmit(event) {
    const form = /** @type {HTMLFormElement} */ (event.target);
    
    // Let browser handle HTML5 validation
    if (!form.checkValidity()) {
      form.reportValidity();
      event.preventDefault();
      return;
    }
    
    // Form is valid - let it submit natively
    // Button will update on page reload via checkContactSuccess()
    // (Product ID was stored in sessionStorage when modal opened)
  }

}

customElements.define('poa-enquiry-modal', POAEnquiryModal);
